import { useState, useEffect } from 'react';
import { centralizedAuth } from '@cimpress/simple-auth-wrapper';
import * as Sentry from '@sentry/react';
import type { Auth0Profile, AuthenticationProps } from '../types/auth';

const CLIENT_ID = 't3T6M4n5Xw8pRV4ZnMXw4Z0qQ9vAv8rc'; // ChatMCP UI

export const authWrapper = new centralizedAuth({
  clientID: CLIENT_ID,
  redirectRoute: '/',
  emitInitialTokenExpired: false,
});

authWrapper.on('tokenExpired', async () => {
  const nextUri = window.location.pathname || '/';
  await authWrapper.login({ nextUri, forceLogin: true });
});

export type { Auth0Profile, AuthenticationProps } from '../types/auth';

export const useAuthentication = (): AuthenticationProps => {
  const [isAuthenticating, setIsAuthenticating] = useState(false);
  const [accessToken, setAccessToken] = useState('');
  const [error, setError] = useState(undefined);
  const [profile, setProfile] = useState(undefined as Auth0Profile | undefined);

  const logout = async () => {
    await authWrapper.logout('/');
  };

  const login = async () => {
    if (!accessToken) {
      const nextUri = window.location.pathname + window.location.search;
      setError(undefined);
      setIsAuthenticating(true);
      try {
        await authWrapper.ensureAuthentication({ nextUri });
        const profile = authWrapper.getProfile() as Auth0Profile;
        const accessToken = authWrapper.getAccessToken() || '';
        setProfile(profile);
        setAccessToken(accessToken);
      } catch (error) {
        // eslint-disable-next-line
        setError(error as any);
      }
      setIsAuthenticating(false);
    }
  };

  useEffect(() => {
    login();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [accessToken]);

  useEffect(() => {
    // To stay under the limit of sessions of the free tiers ignoring these users.
    const ignoredUsers: string[] = [];

    // Set user on reporting apps
    if (
      profile &&
      window.location.hostname !== 'localhost' &&
      !ignoredUsers.includes(profile.email)
    ) {
      // Set up sentry
      Sentry.setUser({
        email: profile.email,
        id: profile.sub,
        username: profile.name,
      });
    }
  }, [profile]);

  return {
    isAuthenticating,
    profile,
    accessToken,
    authenticationError: error,
    login,
    logout,
  };
};
